import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete as MUIAutocomplete,
  CircularProgress,
  InputAdornment,
  SxProps,
  TextField as MUITextField,
  Theme,
  useTheme,
} from "@mui/material";
import { LocationOnOutlined } from "@mui/icons-material";
import FieldError from "../fieldError";

/**
 * Address search / autocomplete powered by the Google Maps JavaScript API
 * (Places library, "new" Places API — AutocompleteSuggestion + Place).
 *
 * Requirements in the Google Cloud Console for the project that owns the key:
 *   - "Maps JavaScript API" enabled
 *   - "Places API (New)" enabled
 *   - The key referrer-restricted to your app's origins
 *
 * The component loads the Maps script lazily on first mount and shares a single
 * session token per "type-then-select" cycle so Google bills it as one session.
 */

// ---------------------------------------------------------------------------
// Minimal local typings for the slice of the Google Places API we use.
// (@types/google.maps is not installed, so we describe only what we touch.)
// ---------------------------------------------------------------------------

interface LatLng {
  lat(): number;
  lng(): number;
}

interface PlaceAddressComponent {
  longText: string | null;
  shortText: string | null;
  types: string[];
}

interface GooglePlace {
  id: string;
  formattedAddress: string | null;
  location: LatLng | null;
  addressComponents: PlaceAddressComponent[] | null;
  displayName: string | null;
  fetchFields(request: { fields: string[] }): Promise<{ place: GooglePlace }>;
}

interface FormattableText {
  text: string;
}

interface PlacePrediction {
  placeId: string;
  text: FormattableText;
  mainText?: FormattableText | null;
  secondaryText?: FormattableText | null;
  toPlace(): GooglePlace;
}

interface AutocompleteSuggestion {
  placePrediction: PlacePrediction | null;
}

interface AutocompleteSuggestionStatic {
  fetchAutocompleteSuggestions(request: {
    input: string;
    sessionToken?: unknown;
    includedPrimaryTypes?: string[];
    includedRegionCodes?: string[];
    language?: string;
    region?: string;
  }): Promise<{ suggestions: AutocompleteSuggestion[] }>;
}

interface SessionTokenConstructor {
  new (): unknown;
}

interface PlacesLibrary {
  AutocompleteSuggestion: AutocompleteSuggestionStatic;
  AutocompleteSessionToken: SessionTokenConstructor;
}

interface GoogleMaps {
  importLibrary(library: "places"): Promise<PlacesLibrary>;
  importLibrary(library: string): Promise<unknown>;
}

const getGoogle = (): { maps?: GoogleMaps } | undefined =>
  (window as unknown as { google?: { maps?: GoogleMaps } }).google;

// ---------------------------------------------------------------------------
// Script loader — one shared promise for the whole app.
// ---------------------------------------------------------------------------

const SCRIPT_ID = "google-maps-places-script";
const READY_CALLBACK = "__vestigezeroGoogleMapsReady";

let bootstrapPromise: Promise<void> | null = null;
let placesLibraryPromise: Promise<PlacesLibrary> | null = null;

/**
 * Loads the Maps bootstrap and resolves once `google.maps.importLibrary` is
 * actually callable.
 *
 * The script at maps/api/js is only a loader: it defines `google.maps.Load`
 * and then injects main.js/common.js/places.js, and `importLibrary` does not
 * exist until *those* have run. `script.onload` fires well before that, so
 * reading `importLibrary` there is a race that loses on a cold cache (i.e. a
 * hard refresh). The `callback` parameter is the only reliable ready signal.
 */
const bootstrapMaps = (apiKey: string): Promise<void> => {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  bootstrapPromise = new Promise<void>((resolve, reject) => {
    // Already fully loaded (a second component mounting, or a hot reload).
    if (getGoogle()?.maps?.importLibrary) {
      resolve();
      return;
    }

    (window as unknown as Record<string, unknown>)[READY_CALLBACK] = resolve;

    // A script from a previous module instance is still in flight; it points
    // at the same global callback we just re-registered, so let it finish.
    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&libraries=places&loading=async&v=weekly&callback=${READY_CALLBACK}`;
    script.onerror = () =>
      reject(new Error("Failed to load the Google Maps script."));
    document.head.appendChild(script);
  }).catch((err) => {
    // Clear the cached promise so a transient failure doesn't become permanent.
    bootstrapPromise = null;
    document.getElementById(SCRIPT_ID)?.remove();
    throw err;
  });

  return bootstrapPromise;
};

const loadPlacesLibrary = (apiKey: string): Promise<PlacesLibrary> => {
  if (placesLibraryPromise) {
    return placesLibraryPromise;
  }

  placesLibraryPromise = bootstrapMaps(apiKey)
    .then(() => {
      const maps = getGoogle()?.maps;
      if (!maps?.importLibrary) {
        throw new Error("Google Maps loaded but importLibrary is unavailable.");
      }
      return maps.importLibrary("places");
    })
    .catch((err) => {
      placesLibraryPromise = null;
      throw err;
    });

  return placesLibraryPromise;
};

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PlaceResult {
  /** Full formatted address, e.g. "1600 Amphitheatre Pkwy, Mountain View, CA". */
  address: string;
  /** Google place id, useful as a stable reference for the location. */
  placeId: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface GooglePlacesSearchProps {
  /** Fired when the user picks a suggestion (after place details are fetched). */
  onSelect: (place: PlaceResult) => void;
  /** Controlled text shown in the input. Defaults to internal state. */
  value?: string;
  /** Fires on every keystroke with the raw input text. */
  onInputChange?: (text: string) => void;
  label?: string;
  placeHolder?: string;
  name?: string;
  size?: "tiny" | "small" | "medium";
  textAlign?: "left" | "center" | "right";
  disabled?: boolean;
  /** External validation error rendered beneath the field. */
  error?: string;
  /** Restrict results to ISO 3166-1 country code(s), e.g. "us" or ["us","ca"]. */
  country?: string | string[];
  /** Place types to include. Defaults to ["geocode"] (addresses + areas). */
  types?: string[];
  /** Minimum characters typed before suggestions are fetched. */
  minChars?: number;
  optionFontSize?: number;
  style?: React.CSSProperties;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const componentValue = (
  components: PlaceAddressComponent[] | null | undefined,
  type: string,
  variant: "long" | "short" = "long",
): string | undefined => {
  const match = components?.find((component) => component.types.includes(type));
  if (!match) {
    return undefined;
  }
  return (variant === "short" ? match.shortText : match.longText) ?? undefined;
};

const toPlaceResult = (
  place: GooglePlace,
  prediction: PlacePrediction,
): PlaceResult => {
  const components = place.addressComponents;
  return {
    address: place.formattedAddress ?? prediction.text.text,
    placeId: place.id || prediction.placeId,
    latitude: place.location?.lat(),
    longitude: place.location?.lng(),
    city:
      componentValue(components, "locality") ??
      componentValue(components, "postal_town") ??
      componentValue(components, "sublocality"),
    state: componentValue(components, "administrative_area_level_1", "short"),
    postalCode: componentValue(components, "postal_code"),
    country: componentValue(components, "country", "short"),
  };
};

const DEBOUNCE_MS = 250;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
  onSelect,
  value,
  onInputChange,
  label = "",
  placeHolder,
  name = "address",
  size = "small",
  textAlign = "left",
  disabled = false,
  error,
  country = "ca",
  types = ["geocode"],
  minChars = 3,
  optionFontSize = 14,
  style = {},
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [internalValue, setInternalValue] = useState("");
  const inputValue = value ?? internalValue;

  const [options, setOptions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Kept apart from loadError: the script can load fine and the suggestion
  // call still be refused (a disabled API, lapsed billing, a key restriction).
  const [searchError, setSearchError] = useState<string | null>(null);

  const libraryRef = useRef<PlacesLibrary | null>(null);
  const sessionTokenRef = useRef<unknown>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

  const includedRegionCodes = useMemo(() => {
    if (!country) {
      return undefined;
    }
    return (Array.isArray(country) ? country : [country]).map((code) =>
      code.toLowerCase(),
    );
  }, [country]);

  const refreshSessionToken = useCallback(() => {
    const library = libraryRef.current;
    sessionTokenRef.current = library
      ? new library.AutocompleteSessionToken()
      : null;
  }, []);

  // Load the Places library once on mount.
  useEffect(() => {
    let active = true;

    if (!apiKey) {
      setLoadError("Missing Google Maps API key.");
      return;
    }

    loadPlacesLibrary(apiKey)
      .then((library) => {
        if (!active) {
          return;
        }
        libraryRef.current = library;
        refreshSessionToken();
        setLoadError(null);
      })
      .catch(() => {
        if (active) {
          setLoadError(t("ui.address_unavailable"));
        }
      });

    return () => {
      active = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [apiKey, refreshSessionToken, t]);

  const fetchPredictions = useCallback(
    async (input: string) => {
      const library = libraryRef.current;
      if (!library || !input.trim()) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const { suggestions } =
          await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionTokenRef.current ?? undefined,
            includedPrimaryTypes: types,
            ...(includedRegionCodes ? { includedRegionCodes } : {}),
          });

        setOptions(
          suggestions
            .map((suggestion) => suggestion.placePrediction)
            .filter(
              (prediction): prediction is PlacePrediction =>
                prediction !== null,
            ),
        );
        setSearchError(null);
      } catch (caught) {
        // Most often: "Places API (New)" not enabled or key restrictions.
        // eslint-disable-next-line no-console
        console.error("Google Places autocomplete failed:", caught);
        setOptions([]);
        // The list is hidden entirely when it is empty (the field is
        // freeSolo, so noOptionsText never renders), which would leave a
        // failure looking exactly like an address with no matches.
        setSearchError(
          "Address search is unavailable right now — you can type your address in full instead.",
        );
      } finally {
        setLoading(false);
      }
    },
    [includedRegionCodes, types],
  );

  const debouncedFetch = useCallback(
    (input: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void fetchPredictions(input);
      }, DEBOUNCE_MS);
    },
    [fetchPredictions],
  );

  const handleInputChange = useCallback(
    (newInput: string, reason: string) => {
      if (value === undefined) {
        setInternalValue(newInput);
      }
      onInputChange?.(newInput);

      if (reason === "input") {
        if (newInput.trim().length >= minChars) {
          debouncedFetch(newInput);
        } else {
          // Below the search threshold — cancel any pending fetch.
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }
          setOptions([]);
          setLoading(false);
          setSearchError(null);
        }
      } else if (reason === "clear") {
        setOptions([]);
        setSearchError(null);
      }
    },
    [debouncedFetch, minChars, onInputChange, value],
  );

  const handleSelect = useCallback(
    async (prediction: PlacePrediction) => {
      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: [
            "formattedAddress",
            "location",
            "addressComponents",
            "displayName",
          ],
        });
        onSelect(toPlaceResult(place, prediction));
      } catch (caught) {
        // eslint-disable-next-line no-console
        console.error("Failed to load place details:", caught);
        setLoadError(t("ui.address_load_failed"));
      } finally {
        // A selection closes the billing session — start a fresh one.
        refreshSessionToken();
      }
    },
    [onSelect, refreshSessionToken, t],
  );

  const muiSize = size === "tiny" ? "small" : size;
  const tinySx: SxProps<Theme> =
    size === "tiny"
      ? {
          "& .MuiInputBase-input": {
            // iOS Safari zooms the page when a focused input renders under
            // 16px, so the tiny size only applies from sm up.
            fontSize: { xs: 16, sm: 15 },
            height: { xs: 16, sm: 15 },
            padding: 1,
          },
          "& .MuiFormLabel-root": {
            marginTop: inputValue ? -0.8 : -1.5,
            fontSize: 15,
            height: 15,
            padding: 1,
          },
        }
      : {};

  return (
    <div style={style}>
      <MUIAutocomplete<PlacePrediction, false, false, true>
        freeSolo
        disabled={disabled || loadError === "Missing Google Maps API key."}
        options={options}
        loading={loading}
        inputValue={inputValue}
        // Server already filters; keep every option Google returns.
        filterOptions={(availableOptions) => availableOptions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.text.text
        }
        isOptionEqualToValue={(option, selected) =>
          option.placeId === selected.placeId
        }
        noOptionsText={
          inputValue.trim().length >= minChars
            ? t("ui.no_matches")
            : t("ui.type_more", { count: minChars })
        }
        onInputChange={(_, newInput, reason) =>
          handleInputChange(newInput, reason)
        }
        onChange={(_, selected) => {
          if (selected && typeof selected !== "string") {
            void handleSelect(selected);
          }
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as { key: string };
          const primary = option.mainText?.text ?? option.text.text;
          const secondary = option.secondaryText?.text;
          return (
            <li
              key={key}
              {...rest}
              style={{ fontSize: optionFontSize, gap: theme.spacing(1) }}
            >
              <LocationOnOutlined
                fontSize="small"
                style={{ color: theme.palette.text.secondary }}
              />
              <span>
                {primary}
                {secondary ? (
                  <span style={{ color: theme.palette.text.secondary }}>
                    {` — ${secondary}`}
                  </span>
                ) : null}
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          <MUITextField
            {...params}
            name={name}
            label={label}
            placeholder={placeHolder ?? t("ui.search_address")}
            variant="outlined"
            size={muiSize}
            sx={tinySx}
            inputProps={{
              ...params.inputProps,
              style: { ...(params.inputProps.style || {}), textAlign },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <InputAdornment position="end">
                      <CircularProgress color="inherit" size={18} />
                    </InputAdornment>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        fullWidth
      />

      <FieldError error={error || loadError || searchError} />
    </div>
  );
};

export default GooglePlacesSearch;
